// MySQL / MariaDB built-in function names emitted by `mysqlResolver` when
// the cursor is at a position that accepts a function call.
//
// MySQL's parser performs case-insensitive function-name lookup, but the
// curated list below stores names as-typed in the manual (UPPER for
// readability) and lower-cases at lookup time. Resolvers dedupe against
// user-defined routine names (case-insensitive) before emission so a stored
// function with the same name as a built-in surfaces only once.
//
// Selection criteria: functions documented in the MySQL Reference Manual
// chapters "Functions and Operators" and "Window Functions" that surface in
// normal user authoring (string / numeric / date-time / aggregate / JSON
// helpers). Operators (`->`, `->>`, `<=>`) are out of scope for completion —
// they surface via Monaco's keyword/snippet path when supported.

const MYSQL_BUILTIN_FUNCTION_LIST: readonly string[] = [
  // Aggregate
  'AVG', 'BIT_AND', 'BIT_OR', 'BIT_XOR', 'COUNT', 'GROUP_CONCAT',
  'JSON_ARRAYAGG', 'JSON_OBJECTAGG', 'MAX', 'MIN', 'STD', 'STDDEV',
  'STDDEV_POP', 'STDDEV_SAMP', 'SUM', 'VAR_POP', 'VAR_SAMP', 'VARIANCE',
  // String
  'ASCII', 'BIN', 'BIT_LENGTH', 'CHAR', 'CHAR_LENGTH', 'CHARACTER_LENGTH',
  'CONCAT', 'CONCAT_WS', 'ELT', 'EXPORT_SET', 'FIELD', 'FIND_IN_SET',
  'FORMAT', 'FROM_BASE64', 'HEX', 'INSERT', 'INSTR', 'LCASE', 'LEFT',
  'LENGTH', 'LIKE', 'LOAD_FILE', 'LOCATE', 'LOWER', 'LPAD', 'LTRIM',
  'MAKE_SET', 'MID', 'OCT', 'OCTET_LENGTH', 'ORD', 'POSITION', 'QUOTE',
  'REGEXP', 'REGEXP_INSTR', 'REGEXP_LIKE', 'REGEXP_REPLACE',
  'REGEXP_SUBSTR', 'REPEAT', 'REPLACE', 'REVERSE', 'RIGHT', 'RPAD',
  'RTRIM', 'SOUNDEX', 'SOUNDS_LIKE', 'SPACE', 'STRCMP', 'SUBSTR',
  'SUBSTRING', 'SUBSTRING_INDEX', 'TO_BASE64', 'TRIM', 'UCASE', 'UNHEX',
  'UPPER',
  // Numeric
  'ABS', 'ACOS', 'ASIN', 'ATAN', 'ATAN2', 'CEIL', 'CEILING', 'CONV',
  'COS', 'COT', 'CRC32', 'DEGREES', 'EXP', 'FLOOR', 'GREATEST', 'LEAST',
  'LN', 'LOG', 'LOG10', 'LOG2', 'MOD', 'PI', 'POW', 'POWER', 'RADIANS',
  'RAND', 'ROUND', 'SIGN', 'SIN', 'SQRT', 'TAN', 'TRUNCATE',
  // Date/time
  'ADDDATE', 'ADDTIME', 'CONVERT_TZ', 'CURDATE', 'CURRENT_DATE',
  'CURRENT_TIME', 'CURRENT_TIMESTAMP', 'CURTIME', 'DATE', 'DATE_ADD',
  'DATE_FORMAT', 'DATE_SUB', 'DATEDIFF', 'DAY', 'DAYNAME', 'DAYOFMONTH',
  'DAYOFWEEK', 'DAYOFYEAR', 'EXTRACT', 'FROM_DAYS', 'FROM_UNIXTIME',
  'GET_FORMAT', 'HOUR', 'LAST_DAY', 'LOCALTIME', 'LOCALTIMESTAMP',
  'MAKEDATE', 'MAKETIME', 'MICROSECOND', 'MINUTE', 'MONTH', 'MONTHNAME',
  'NOW', 'PERIOD_ADD', 'PERIOD_DIFF', 'QUARTER', 'SEC_TO_TIME', 'SECOND',
  'STR_TO_DATE', 'SUBDATE', 'SUBTIME', 'SYSDATE', 'TIME', 'TIME_FORMAT',
  'TIME_TO_SEC', 'TIMEDIFF', 'TIMESTAMP', 'TIMESTAMPADD', 'TIMESTAMPDIFF',
  'TO_DAYS', 'TO_SECONDS', 'UNIX_TIMESTAMP', 'UTC_DATE', 'UTC_TIME',
  'UTC_TIMESTAMP', 'WEEK', 'WEEKDAY', 'WEEKOFYEAR', 'YEAR', 'YEARWEEK',
  // Conditional / null
  'COALESCE', 'IF', 'IFNULL', 'ISNULL', 'NULLIF',
  // Type casting / formatting
  'BINARY', 'CAST', 'CONVERT',
  // JSON
  'JSON_ARRAY', 'JSON_ARRAY_APPEND', 'JSON_ARRAY_INSERT',
  'JSON_CONTAINS', 'JSON_CONTAINS_PATH', 'JSON_DEPTH', 'JSON_EXTRACT',
  'JSON_INSERT', 'JSON_KEYS', 'JSON_LENGTH', 'JSON_MERGE',
  'JSON_MERGE_PATCH', 'JSON_MERGE_PRESERVE', 'JSON_OBJECT', 'JSON_PRETTY',
  'JSON_QUOTE', 'JSON_REMOVE', 'JSON_REPLACE', 'JSON_SEARCH', 'JSON_SET',
  'JSON_TABLE', 'JSON_TYPE', 'JSON_UNQUOTE', 'JSON_VALID', 'JSON_VALUE',
  // System / introspection
  'CONNECTION_ID', 'CURRENT_USER', 'DATABASE', 'LAST_INSERT_ID',
  'ROW_COUNT', 'SCHEMA', 'SESSION_USER', 'SYSTEM_USER', 'USER',
  'UUID', 'UUID_SHORT', 'VERSION',
  // Window
  'CUME_DIST', 'DENSE_RANK', 'FIRST_VALUE', 'LAG', 'LAST_VALUE', 'LEAD',
  'NTH_VALUE', 'NTILE', 'PERCENT_RANK', 'RANK', 'ROW_NUMBER',
  // Hash / encryption
  'AES_DECRYPT', 'AES_ENCRYPT', 'COMPRESS', 'MD5', 'PASSWORD',
  'RANDOM_BYTES', 'SHA', 'SHA1', 'SHA2', 'UNCOMPRESS', 'UNCOMPRESSED_LENGTH',
  // Bit / locking
  'BIT_COUNT', 'GET_LOCK', 'IS_FREE_LOCK', 'IS_USED_LOCK', 'RELEASE_LOCK',
];

export const MYSQL_BUILTIN_FUNCTIONS: ReadonlySet<string> = new Set(
  MYSQL_BUILTIN_FUNCTION_LIST.map((name) => name.toLowerCase()),
);

/** Lazy lookup; case-insensitive match against the curated set. */
export function isMysqlBuiltinFunction(name: string): boolean {
  return MYSQL_BUILTIN_FUNCTIONS.has(name.toLowerCase());
}

/**
 * The full curated list (UPPER per manual convention). Resolver consumers
 * dedupe against the user's schema routines (case-insensitive) before
 * emission.
 */
export function getMysqlBuiltinFunctions(): readonly string[] {
  return MYSQL_BUILTIN_FUNCTION_LIST;
}
